import { DateTime } from 'luxon';

import i18n from '@sonar/i18n';
import { TimeAggregate } from '@sonar/models';

/**
 * Converts a string from hyphen-separated or snake_case to camelCase
 */
export const toCamelCase = (string: string): string =>
  string.replace(/[-|_](\w)/g, str => str[1].toUpperCase());

/**
 * Converts a luxon datetime object to short format
 * see https://moment.github.io/luxon/docs/manual/formatting.html#toformat
 */
export const formatDateToShort = (date: DateTime): string =>
  date.toLocaleString({
    month: 'short',
    day: 'numeric',
  });

const firstWeekLetter = i18n.t(`period.aggregate.week`).charAt(0);

/**
 * Format date according to data value
 */
export const formatDate = (
  dateToFormat: Date | DateTime | string,
  aggregate: TimeAggregate,
  locale: string,
): string => {
  const dateTime =
    typeof dateToFormat === 'string'
      ? DateTime.fromISO(dateToFormat)
      : dateToFormat instanceof Date
      ? DateTime.fromJSDate(dateToFormat)
      : dateToFormat;
  const localeDateTime = dateTime.setLocale(locale);

  const dateFormatted: Record<string, (date: DateTime) => string> = {
    hour: date => date.toLocaleString({ hour: 'numeric' }),
    day: date => date.toLocaleString({ month: 'numeric', day: 'numeric' }),
    week: date => date.toFormat(`WW '${firstWeekLetter}'`),
    month: date => date.toLocaleString({ month: 'short' }),
    sevenDays: date => date.toLocaleString({ month: 'numeric', day: 'numeric' }),
    default: date => date.toLocaleString({ month: 'numeric', day: 'numeric' }),
  };

  return dateFormatted[aggregate](localeDateTime) || dateFormatted.default(localeDateTime);
};
